// ============================================================================
// Login Page — email/password form with new-password-required handling
// ============================================================================

import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

export function LoginPage() {
  const { login, completeNewPassword, error: authError, isLoading, needsNewPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError('Email and password are required');
      return;
    }

    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!newPassword) {
      setError('New password is required');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      await completeNewPassword(newPassword);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set new password');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-brand-green to-brand-green-700 px-4">
      <div className="w-full max-w-sm">
        {/* Logo / Title */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-white text-brand-green font-bold text-xl shadow-lg">
            CCM
          </div>
          <h1 className="text-2xl font-bold text-white">
            Social Media Manager
          </h1>
          <p className="mt-1 text-sm text-brand-green-200">
            Cream City Mortgage
          </p>
        </div>

        {/* New Password Form (first login with temp password) */}
        {needsNewPassword ? (
          <form
            onSubmit={handleNewPassword}
            className="card space-y-5 p-6 shadow-xl"
          >
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Set Your Password</h2>
              <p className="mt-1 text-sm text-gray-500">
                Your account was created with a temporary password. Please choose a new one.
              </p>
            </div>

            <div>
              <label htmlFor="newPassword" className="label">
                New Password
              </label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input mt-1"
                placeholder="At least 8 characters"
                autoComplete="new-password"
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="label">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input mt-1"
                placeholder="Confirm your password"
                autoComplete="new-password"
              />
            </div>

            {(error || authError) && (
              <p className="text-sm font-medium text-red-600">
                {error || authError}
              </p>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-2.5"
            >
              {isLoading ? 'Setting password...' : 'Set Password & Sign In'}
            </button>

            <p className="text-center text-xs text-gray-400">
              Requires: 8+ characters, uppercase, lowercase, number
            </p>
          </form>
        ) : (
          /* Login Form */
          <form
            onSubmit={handleLogin}
            className="card space-y-5 p-6 shadow-xl"
          >
            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input mt-1"
                placeholder="you@creamcitymortgage.com"
                autoComplete="email"
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input mt-1"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>

            {(error || authError) && (
              <p className="text-sm font-medium text-red-600">
                {error || authError}
              </p>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-2.5"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
